/**
 * AnonOverdueBanner — the cross-dashboard "flag" for anonymous reports
 * that have blown their act-by deadline.
 *
 * Rendered on all three dashboards (MSS admin, group, school). The
 * /admin/anon-overdue endpoint already scopes the count to the caller's
 * role, so the same component shows the right number everywhere:
 *   • MSS / super  → overdue across every school
 *   • group admin  → overdue across their group
 *   • school admin → overdue at their school
 *
 * Renders nothing when there's nothing overdue — it's a flag, not a
 * permanent fixture. Pass `linkTo` to make the whole banner a link
 * (the school dashboard points it at /anon-reports).
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Flag, ArrowRight } from 'lucide-react';
import { getAnonOverdue } from '../services/adminApi';
import './AnonOverdueBanner.css';

const AnonOverdueBanner = ({ linkTo }) => {
  const { data } = useQuery({
    queryKey: ['anon', 'overdue'],
    queryFn: getAnonOverdue,
    staleTime: 60 * 1000,
    // A failure here (e.g. backend not yet deployed) must never break
    // the dashboard — just render nothing.
    retry: 1,
  });

  // /admin/anon-overdue now returns the full summary; `overdue` is the
  // past-SLA count this banner flags.
  const overdue = data?.overdue || 0;
  if (overdue === 0) return null;

  const schoolCount = Object.keys(data?.bySchool || {}).length;

  const body = (
    <>
      <span className="anon-overdue-banner-icon"><Flag size={18} /></span>
      <span className="anon-overdue-banner-text">
        <strong>{overdue} anonymous report{overdue === 1 ? '' : 's'}</strong>
        {' '}{overdue === 1 ? 'is' : 'are'} past the act-by deadline
        {schoolCount > 1 ? ` across ${schoolCount} schools` : ''} —
        the wellness council needs to act.
      </span>
      {linkTo && <ArrowRight size={16} className="anon-overdue-banner-arrow" />}
    </>
  );

  if (linkTo) {
    return <Link to={linkTo} className="anon-overdue-banner anon-overdue-banner-link">{body}</Link>;
  }
  return <div className="anon-overdue-banner">{body}</div>;
};

export default AnonOverdueBanner;
